import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Search, Sparkles } from 'lucide-react'

import { Pagination } from '@/components/v2/ui'
import { EID } from '@/lib/element-ids.registry'
import { useListingAudits } from '@/hooks/useListingBuilder'

import { ListingAuditCard } from './ListingAuditCard'
import { ListingBuilderOverviewHeader } from './ListingBuilderOverviewHeader'
import { ListingSourceDialog } from './source/ListingSourceDialog'

const PAGE_SIZE = 24

export function ListingBuilderOverview() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [dialogOpen, setDialogOpen] = useState(false)

  const { data, isLoading } = useListingAudits({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    search: search || undefined,
  })

  const total = data?.total ?? 0
  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0

  return (
    <div id={EID['listing-builder-overview']} className="space-y-6 p-6">
      <ListingBuilderOverviewHeader onNewAudit={() => setDialogOpen(true)} />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <input
          id={EID['listing-builder-search-input']}
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          placeholder={t('listingBuilder.overview.searchPlaceholder')}
          className="w-full rounded-lg border border-neutral-200 bg-white py-2.5 pl-10 pr-4 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 dark:border-neutral-700 dark:bg-neutral-800"
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-xl bg-neutral-100 dark:bg-neutral-800" />
          ))}
        </div>
      ) : data?.items?.length ? (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {data.items.map((audit) => (
              <ListingAuditCard key={audit.id} audit={audit} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-end border-t border-neutral-200 pt-4 dark:border-neutral-800">
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
        </>
      ) : (
        <div className="py-16 text-center">
          <Sparkles className="mx-auto mb-4 h-12 w-12 text-neutral-300 dark:text-neutral-600" />
          <h3 className="text-lg font-semibold text-neutral-700 dark:text-neutral-300">
            {t('listingBuilder.overview.empty')}
          </h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-neutral-500 dark:text-neutral-400">
            {t('listingBuilder.overview.emptyDesc')}
          </p>
        </div>
      )}

      <ListingSourceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onStarted={(jobId) => navigate(`/v2/tools/listing-builder/${jobId}`)}
      />
    </div>
  )
}
